import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", href: "#home" },
  { label: "Collection", href: "#collection" },
  { label: "Wholesale Benefits", href: "#advantages" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact Us", href: "#contact" },
];

const BrandMark = () => (
  <span className="inline-flex items-center gap-3" data-testid="brand-logo">
    <img
      src="/sge-logo.png"
      alt="Shri Gurunath Enterprises logo"
      className="h-12 w-auto object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
      loading="eager"
      decoding="async"
    />
    <span className="flex flex-col leading-none">
      <span className="font-display text-[14px] sm:text-[15px] font-semibold tracking-[0.16em] text-white">
        SHRI GURUNATH
      </span>
      <span className="text-[9px] sm:text-[10px] tracking-[0.30em] text-amber-500/90 font-sans mt-0.5">
        ENTERPRISES
      </span>
    </span>
  </span>
);

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeAndGo = (href) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-slate-900/90 backdrop-blur-xl border-b border-white/10"
          : "bg-slate-900/40 backdrop-blur-md border-b border-white/5"
      }`}
      data-testid="site-navbar"
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-[72px] flex items-center justify-between gap-4">
        <a href="#home" onClick={(e) => { e.preventDefault(); closeAndGo("#home"); }} aria-label="Shri Gurunath Enterprises home" className="shrink-0">
          <BrandMark />
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => { e.preventDefault(); closeAndGo(l.href); }}
              className="text-[11.5px] xl:text-[12px] tracking-[0.18em] uppercase text-white/80 hover:text-amber-400 transition-colors duration-300 whitespace-nowrap"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4 xl:gap-5 shrink-0">
          <a
            href="tel:09845048406"
            className="group inline-flex items-center gap-2 text-white hover:text-amber-400 transition-colors whitespace-nowrap"
            data-testid="nav-phone-link"
          >
            <Phone className="h-3.5 w-3.5 text-amber-500 group-hover:rotate-[10deg] transition-transform" />
            <span className="text-[11.5px] xl:text-[12px] tracking-[0.18em] uppercase">098450 48406</span>
          </a>
          <button
            onClick={() => closeAndGo("#inquiry")}
            className="glow-amber bg-amber-700 hover:bg-amber-800 text-white px-4 xl:px-5 py-2.5 text-[10.5px] xl:text-[11px] tracking-[0.18em] uppercase inline-flex items-center gap-2 transition-colors whitespace-nowrap"
            data-testid="nav-cta-wholesale-pricing"
          >
            <span className="xl:hidden">Get Pricing</span>
            <span className="hidden xl:inline">Get Wholesale Pricing</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <button
          className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-sm border border-white/15 text-white shrink-0"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="nav-hamburger-open"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile Drawer — rendered via portal to escape the navbar's backdrop-filter containing block */}
      {createPortal(
        <div
          className={`lg:hidden fixed inset-0 z-[100] ${open ? "pointer-events-auto" : "pointer-events-none"}`}
          data-testid="mobile-drawer"
        >
          <div
            className={`absolute inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
            onClick={() => setOpen(false)}
          />
          <aside
            className={`absolute right-0 top-0 h-full w-[92%] max-w-sm bg-slate-950 border-l border-white/10 p-6 shadow-[0_0_60px_rgba(0,0,0,0.6)] flex flex-col overflow-y-auto transform transition-transform duration-500 will-change-transform ${
              open ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between mb-10 shrink-0">
              <BrandMark />
              <button
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                data-testid="nav-hamburger-close"
                className="h-11 w-11 inline-flex items-center justify-center text-white/80 hover:text-amber-400 border border-white/10 hover:border-amber-400/50 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => { e.preventDefault(); closeAndGo(l.href); }}
                  className="py-4 border-b border-white/5 text-white/90 hover:text-amber-400 text-[14px] tracking-[0.18em] uppercase"
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
            </nav>
            <div className="mt-10 space-y-4 shrink-0">
              <a
                href="tel:09845048406"
                className="flex items-center gap-3 text-white/80 py-3"
                data-testid="mobile-nav-phone"
              >
                <Phone className="h-4 w-4 text-amber-500" />
                <span className="text-sm">098450 48406</span>
              </a>
              <button
                onClick={() => closeAndGo("#inquiry")}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white px-5 py-4 text-[12px] tracking-[0.22em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="mobile-cta-wholesale-pricing"
              >
                Get Wholesale Pricing
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </aside>
        </div>,
        document.body
      )}
    </header>
  );
}
